<template>
  <div class="view-records-page">
    <h2 class="page-title">浏览记录</h2>

    <!-- 统计概览 -->
    <div class="stats-row" v-loading="statsLoading">
      <div class="stat-card">
        <div class="stat-icon" style="background:#409EFF">
          <i class="el-icon-view"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.total_views || 0 }}</span>
          <span class="stat-desc">总浏览量</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background:#67C23A">
          <i class="el-icon-date"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.today_views || 0 }}</span>
          <span class="stat-desc">今日浏览</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background:#E6A23C">
          <i class="el-icon-user"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.unique_ips || 0 }}</span>
          <span class="stat-desc">独立访客</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background:#F56C6C">
          <i class="el-icon-mobile"></i>
        </div>
        <div class="stat-content">
          <span class="stat-value">{{ stats.today_unique_ips || 0 }}</span>
          <span class="stat-desc">今日独立访客</span>
        </div>
      </div>
    </div>

    <!-- 近7天浏览趋势 -->
    <div class="chart-section" v-if="stats.daily_stats && stats.daily_stats.length">
      <h3 class="section-title">近7天浏览趋势</h3>
      <div class="daily-chart">
        <div v-for="day in stats.daily_stats" :key="day.date" class="chart-bar">
          <div class="bar-fill" :style="{ height: getBarHeight(day.views) }"></div>
          <span class="bar-label">{{ formatDay(day.date) }}</span>
          <span class="bar-value">{{ day.views }}</span>
        </div>
      </div>
    </div>

    <!-- 热门文章 -->
    <div class="top-section" v-if="stats.top_articles && stats.top_articles.length">
      <h3 class="section-title">热门文章</h3>
      <el-table :data="stats.top_articles" stripe size="small">
        <el-table-column prop="title" label="文章标题" min-width="200" />
        <el-table-column prop="view_count" label="浏览次数" width="100" />
      </el-table>
    </div>

    <!-- 热门页面 -->
    <div class="top-section" v-if="stats.top_pages && stats.top_pages.length">
      <h3 class="section-title">热门页面</h3>
      <el-table :data="stats.top_pages" stripe size="small">
        <el-table-column prop="path" label="访问路径" min-width="200" />
        <el-table-column prop="view_count" label="浏览次数" width="100" />
      </el-table>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-bar">
      <el-input v-model="filterIp" placeholder="搜索 IP" size="small" clearable style="width:160px;margin-right:12px" />
      <el-input v-model="filterPath" placeholder="搜索路径" size="small" clearable style="width:200px;margin-right:12px" />
      <el-button type="primary" size="small" icon="el-icon-search" @click="loadRecords">查询</el-button>
      <el-button type="danger" size="small" icon="el-icon-delete" @click="handleClearAll">清空记录</el-button>
    </div>

    <!-- 浏览记录列表 -->
    <div class="records-table" v-loading="recordsLoading">
      <el-table :data="records" stripe size="small">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="visitor_ip" label="访客IP" width="140" />
        <el-table-column label="访问路径" min-width="180">
          <template slot-scope="scope">
            <span>{{ scope.row.path || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="关联文章" min-width="180">
          <template slot-scope="scope">
            <span v-if="scope.row.article_title">{{ scope.row.article_title }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="referer" label="来源" width="140">
          <template slot-scope="scope">
            <span class="truncate-cell">{{ scope.row.referer || '直接访问' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="设备" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.is_mobile ? 'warning' : 'success'" size="mini">
              {{ scope.row.is_mobile ? '移动' : 'PC' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="浏览时长" width="90">
          <template slot-scope="scope">
            {{ scope.row.visit_duration > 0 ? scope.row.visit_duration + '秒' : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="时间" width="140">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="70">
          <template slot-scope="scope">
            <el-button type="text" size="mini" icon="el-icon-delete" @click="handleDelete(scope.row)" />
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row" v-if="pagination.total > 0">
        <el-pagination
          layout="total, prev, pager, next"
          :total="pagination.total"
          :page-size="pagination.per_page"
          :current-page.sync="pagination.page"
          @current-change="loadRecords"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '../../utils/api'

export default {
  name: 'AdminViewRecords',
  data() {
    return {
      stats: {},
      statsLoading: false,
      records: [],
      recordsLoading: false,
      pagination: { page: 1, per_page: 20, total: 0 },
      filterIp: '',
      filterPath: ''
    }
  },
  created() {
    this.loadStats()
    this.loadRecords()
  },
  methods: {
    async loadStats() {
      this.statsLoading = true
      try {
        this.stats = await adminApi.getViewStats()
      } catch (e) {
        console.error(e)
      }
      this.statsLoading = false
    },
    async loadRecords() {
      this.recordsLoading = true
      try {
        const params = {
          page: this.pagination.page,
          per_page: this.pagination.per_page
        }
        if (this.filterIp) params.ip = this.filterIp
        if (this.filterPath) params.path = this.filterPath
        const res = await adminApi.getViewRecords(params)
        this.records = res.records
        this.pagination = res.pagination
      } catch (e) {
        console.error(e)
      }
      this.recordsLoading = false
    },
    handleDelete(row) {
      this.$confirm('确认删除此条浏览记录？', '提示', { type: 'warning' }).then(async () => {
        try {
          await adminApi.deleteViewRecord(row.id)
          this.$message.success('已删除')
          this.loadRecords()
        } catch (e) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    handleClearAll() {
      this.$confirm('确认清空所有浏览记录？此操作不可恢复！', '警告', { type: 'warning' }).then(async () => {
        try {
          await adminApi.clearViewRecords()
          this.$message.success('已清空所有浏览记录')
          this.loadStats()
          this.loadRecords()
        } catch (e) {
          this.$message.error('清空失败')
        }
      }).catch(() => {})
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
    },
    formatDay(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return `${d.getMonth()+1}/${d.getDate()}`
    },
    getBarHeight(views) {
      const maxViews = Math.max(...(this.stats.daily_stats || []).map(d => d.views), 1)
      const pct = Math.max((views / maxViews) * 100, 4)
      return pct + '%'
    }
  }
}
</script>

<style scoped>
.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
}
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}
.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  border: 1px solid #e8e8e8;
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  margin-right: 16px;
}
.stat-content {
  display: flex;
  flex-direction: column;
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
}
.stat-desc {
  font-size: 13px;
  color: #999;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
}
.chart-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
}
.daily-chart {
  display: flex;
  align-items: flex-end;
  height: 160px;
  gap: 12px;
  padding: 10px 0;
}
.chart-bar {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.bar-fill {
  width: 100%;
  max-width: 40px;
  background: linear-gradient(180deg, #409EFF, #66b1ff);
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: height 0.3s;
}
.bar-label {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}
.bar-value {
  font-size: 11px;
  color: #606266;
  margin-top: 2px;
}
.top-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
}
.filter-bar {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}
.records-table {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8e8e8;
}
.pagination-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.truncate-cell {
  display: inline-block;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
  .filter-bar {
    flex-direction: column;
  }
}
</style>
